import { useTranslation } from "react-i18next";

const StatsCard = ({
  title,
  value,
  footer,
  trend,
  reverseTrendColor,
  showCurrency = true,
  isLoading = false,
}) => {
  const { i18n } = useTranslation();

  const trendColor = (() => {
    if (!trend) return "gray";

    if (trend.direction === "no-change" || trend.change === 0) return "gray";

    if (reverseTrendColor) {
      return trend.direction === "up" ? "red" : "green";
    }

    return trend.color;
  })();

  function formatNumber(value, options = {}) {
    const numericValue = typeof value === "string" ? Number(value) : value;

    if (typeof numericValue !== "number" || isNaN(numericValue)) {
      return value;
    }

    const locale = i18n.language || "en-US";
    try {
      if (!showCurrency) {
        return new Intl.NumberFormat(locale, {
          notation: "compact",
          compactDisplay: "short",
          ...options,
        }).format(numericValue);
      }

      return new Intl.NumberFormat(locale, {
        style: "currency",
        notation: "compact",
        compactDisplay: "short",
        currency: "IDR",
        ...options,
      }).format(numericValue);
    } catch (error) {
      return value;
    }
  }

  const getTrendIcon = () => {
    if (!trend) return null;

    const { direction } = trend;

    if (direction === "no-change") {
      return null;
    }

    const isUp = direction === "up";

    return (
      <svg
        className={`self-center flex-shrink-0 h-5 w-5 text-${trendColor}-500`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d={
            isUp
              ? "M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z"
              : "M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z"
          }
          clipRule="evenodd"
        />
      </svg>
    );
  };

  if (isLoading) {
    return (
      <div className="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6 animate-pulse">
        <div className="h-3 bg-gray-200 rounded w-1/3 mb-4" />
        <div className="h-8 bg-gray-200 rounded w-2/3" />
      </div>
    );
  }

  return (
    <div className="px-4 py-5 bg-white shadow rounded-lg overflow-hidden sm:p-6">
      <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
      <dd className="mt-1 text-3xl font-semibold text-gray-900">
        {formatNumber(value)}
      </dd>
      {footer && (
        <dd
          className={`mt-2 flex items-center text-sm ${
            trendColor ? `text-${trendColor}-600` : "text-gray-600"
          }`}
        >
          {trend && getTrendIcon()}
          <span
            className={trend && trend.direction !== "no-change" ? "ml-2" : ""}
          >
            {footer}
          </span>
        </dd>
      )}
    </div>
  );
};

export default StatsCard;
